import type { MedplumClient } from '@medplum/core';
import type { Bundle, Goal, Observation, Provenance } from '@medplum/fhirtypes';

import {
  type FhirConnectionState,
  type FhirQueryState,
  useFhirConnection,
  useFhirData,
} from '@eluve/fhir-data';
import { flattenFhirGraphQLData } from '@eluve/fhir-renderer';
import { type ResultOf } from '@eluve/graphql-fhir';

import {
  REVIEW_STATUS_ATTESTED,
  REVIEW_STATUS_REJECTED,
  isPendingReview,
  setReviewStatusTag,
} from '../review-markers';

import {
  FHIR_PATIENT_ID_QUERY,
  PATIENT_GOALS_QUERY,
  TRACKED_OBSERVATIONS_QUERY,
} from './fhirQueries';

/**
 * Data layer for the patient-profile Goals card: reads via the FHIR GraphQL
 * connection, review writes via the Medplum SDK client. The two paths share
 * no cache — callers refetch the read after a write lands.
 */

function mapPatientId(
  data: ResultOf<typeof FHIR_PATIENT_ID_QUERY>,
): string | null {
  return data.PatientList?.[0]?.id ?? null;
}

/** Resolve the FHIR Patient id for an Eluve (Hasura) patient id. */
export function useFhirPatientId(
  patientId: string,
): FhirQueryState<string | null> {
  return useFhirData(FHIR_PATIENT_ID_QUERY, mapPatientId, {
    variables: { identifier: patientId },
  });
}

function mapGoals(data: ResultOf<typeof PATIENT_GOALS_QUERY>): Goal[] {
  return flattenFhirGraphQLData(data).filter(
    (resource): resource is Goal & { id: string } =>
      resource.resourceType === 'Goal' && Boolean(resource.id),
  );
}

/** The patient's goals, paged. Skipped until the FHIR id resolves. */
export function usePatientGoals(
  fhirPatientId: string | null,
): FhirConnectionState<Goal[]> {
  return useFhirConnection(PATIENT_GOALS_QUERY, 'GoalConnection', mapGoals, {
    variables: { patientRef: `Patient/${fhirPatientId}` },
    skip: !fhirPatientId,
  });
}

function mapObservations(
  data: ResultOf<typeof TRACKED_OBSERVATIONS_QUERY>,
): Observation[] {
  return flattenFhirGraphQLData(data).filter(
    (resource): resource is Observation & { id: string } =>
      resource.resourceType === 'Observation' && Boolean(resource.id),
  );
}

/**
 * The observation window the goal rows resolve tracked series from. Loaded
 * alongside the goals; a miss just means no sparkline for that goal.
 */
export function useTrackedObservations(
  fhirPatientId: string | null,
): FhirConnectionState<Observation[]> {
  return useFhirConnection(
    TRACKED_OBSERVATIONS_QUERY,
    'ObservationConnection',
    mapObservations,
    {
      variables: { patientRef: `Patient/${fhirPatientId}` },
      skip: !fhirPatientId,
    },
  );
}

export type ReviewDecision =
  | { kind: 'accept'; editedDisplay?: string }
  | { kind: 'dismiss' };

/**
 * Apply a reviewer's decision to a pending AI-drafted goal — the same
 * transitions the backend review capability performs
 * (`clinical-findings/finding-types/goal.ts`): re-read, verify still
 * pending, flip the `lifecycleStatus` + review-status tag, and record an
 * attestation Provenance, all in one transaction so the audit trail can't
 * drift from the decision.
 *
 * - accept → lifecycleStatus `accepted` (the backend `confirm()`),
 *   tag `attested`.
 * - accept with an edited description → `description` replaced by the
 *   free-text wording (a goal has no coding to edit — exactly the backend
 *   `applyEdit()`), then accepted as above.
 * - dismiss → lifecycleStatus `entered-in-error` (the backend `retract()`),
 *   tag `rejected`.
 */
export async function reviewGoal(
  client: MedplumClient,
  args: {
    goalId: string;
    decision: ReviewDecision;
    /** `Practitioner/<id>` recorded as the verifier on the Provenance. */
    reviewerReference: string | null;
  },
): Promise<void> {
  const { goalId, decision, reviewerReference } = args;
  const current = await client.readResource('Goal', goalId);
  if (!isPendingReview(current)) {
    throw new Error('This goal is no longer awaiting review.');
  }

  let next: Goal;
  if (decision.kind === 'dismiss') {
    next = setReviewStatusTag(
      { ...current, lifecycleStatus: 'entered-in-error' },
      REVIEW_STATUS_REJECTED,
    );
  } else {
    const edited: Goal =
      decision.editedDisplay !== undefined
        ? { ...current, description: { text: decision.editedDisplay } }
        : current;
    next = setReviewStatusTag(
      { ...edited, lifecycleStatus: 'accepted' },
      REVIEW_STATUS_ATTESTED,
    );
  }

  const entries: NonNullable<Bundle['entry']> = [
    {
      request: { method: 'PUT', url: `Goal/${goalId}` },
      resource: next,
    },
  ];
  if (decision.kind === 'accept' && reviewerReference) {
    entries.push({
      request: { method: 'POST', url: 'Provenance' },
      resource: buildAttestationProvenance(`Goal/${goalId}`, reviewerReference),
    });
  }

  const response = await client.executeBatch({
    resourceType: 'Bundle',
    type: 'transaction',
    entry: entries,
  });
  assertNoFailedEntries(response);
}

const DATA_OPERATION_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/v3-DataOperation';
const PROVENANCE_PARTICIPANT_TYPE_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/provenance-participant-type';

/** Attestation Provenance: the reviewer verified this finding. */
function buildAttestationProvenance(
  target: string,
  reviewerReference: string,
): Provenance {
  return {
    resourceType: 'Provenance',
    target: [{ reference: target }],
    recorded: new Date().toISOString(),
    activity: {
      coding: [
        { system: DATA_OPERATION_SYSTEM, code: 'UPDATE', display: 'revise' },
      ],
    },
    agent: [
      {
        type: {
          coding: [
            { system: PROVENANCE_PARTICIPANT_TYPE_SYSTEM, code: 'verifier' },
          ],
        },
        who: { reference: reviewerReference },
      },
    ],
  };
}

function assertNoFailedEntries(response: Bundle): void {
  const failed = (response.entry ?? []).filter((entry) => {
    const code = Number.parseInt(entry.response?.status ?? '', 10);
    return !(code >= 200 && code < 300);
  });
  if (failed.length > 0) {
    throw new Error(
      `Review write failed: ${failed
        .map((entry) => entry.response?.status ?? 'unknown')
        .join(', ')}`,
    );
  }
}
